import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
  Paper,
  Tooltip,
  Button,
} from "@mui/material";
import { ArrowBack, Logout } from "@mui/icons-material";
import { useTranslate } from "../Translate/TranslateContext";
import {
  getUserSessions,
  deleteUserSession,
  revokeAllSessions,
} from "../../api/userSessions";
import axios from "axios";

// Parses browser and OS info from the user agent
function parseUserAgent(ua = "") {
  if (!ua) return { os: "Unknown OS", browser: "Unknown Browser" };

  try {
    const browser = ua.includes("Edg/")
      ? "Microsoft Edge"
      : ua.includes("Chrome/") && !ua.includes("Chromium")
      ? "Chrome"
      : ua.includes("Firefox/")
      ? "Firefox"
      : ua.includes("Safari/") && !ua.includes("Chrome/")
      ? "Safari"
      : "Unknown Browser";

    const osMatch = ua.match(/\(([^)]+)\)/);
    let os = osMatch ? osMatch[1] : "Unknown OS";

    os = os
      .replace(/Windows NT 10\.0/gi, "Windows 10")
      .replace(/Windows NT 11\.0/gi, "Windows 11")
      .replace(/Windows NT 6\.3/gi, "Windows 8.1")
      .replace(/Windows NT 6\.2/gi, "Windows 8")
      .replace(/Windows NT 6\.1/gi, "Windows 7")
      .replace(/Windows NT [0-9.]+/gi, "Windows")
      .replace(/Mac OS X ([\d_]+)/gi, (_, v) => "macOS " + v.replace(/_/g, "."))
      .replace(/;.*$/, "")
      .trim();

    return { os, browser };
  } catch {
    return { os: "Unknown OS", browser: "Unknown Browser" };
  }
}

export default function DeviceSettings({ onBack, user, onLogout }) {
  const { language, translate } = useTranslate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSession, setCurrentSession] = useState(null);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    (async () => {
      const [sessionsRes, currentRes] = await Promise.allSettled([
        getUserSessions(user.user_id),
        axios.get("/api/auth/user", { withCredentials: true }),
      ]);

      if (sessionsRes.value?.success) setSessions(sessionsRes.value.sessions);
      if (currentRes.value?.data?.user?.session_id)
        setCurrentSession(currentRes.value.data.user.session_id);

      setLoading(false);
    })();
  }, [user]);

  const handleDelete = async (id) => {
    const res = await deleteUserSession(id);
    if (res?.success) setSessions((prev) => prev.filter((s) => s.id !== id));
  };

  const handleRevokeAll = async () => {
    setRevoking(true);
    const res = await revokeAllSessions(user.user_id);
    if (res?.success) {
      setSessions([]);
      onLogout(); 
    }
    setRevoking(false);
  };

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={onBack}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {translate("manage_devices")}
          </Typography>
        </Box>

       {!loading && (
  <Button
    variant="outlined"
    color="error"
    size="small"
    onClick={handleRevokeAll}
    startIcon={<Logout />}
    disabled={revoking}
  >
    {translate("logout_all_devices")}
  </Button>
)}
      </Box>

      <Divider sx={{ my: 2 }} />

      {loading ? (
        <CircularProgress size={24} />
      ) : sessions.length === 0 ? (
        <Typography variant="body2">
          {translate("no_active_devices")}
        </Typography>
      ) : (
        <Box display="flex" flexDirection="column" gap={1.5} sx={{
      maxHeight: 400,
      overflowY: "auto",
      pr: 1,
    }} >
          {sessions.map((s) => {
            const { os, browser } = parseUserAgent(s.user_agent);
      const lastSeenDate = new Date(s.last_seen);
const lastSeen = new Intl.DateTimeFormat(
  language || "en",
  {
    dateStyle: "medium",
    timeStyle: "short",
  }
).format(lastSeenDate);


            const isCurrent = currentSession && s.session_id === currentSession;

            return (
              <Paper
                key={s.id}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: isCurrent
                    ? "rgba(76,175,80,0.08)"
                    : "#fafafa",
                  borderColor: isCurrent ? "#4caf50" : "#ddd",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderWidth: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {browser} {"\u00B7"} {os}
                    {isCurrent && (
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 700,
                          color: "success.main",
                          ml: 0.5,
                        }}
                      >
                        ({translate("you")})
                      </Typography>
                    )}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">

                    {translate("signed_in_from")}:{" "}
                    {s.location_label || translate("unknown_location")}
                </Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
                  >
                    {translate("last_seen")}: {lastSeen}
                  </Typography>
                </Box>

                {!isCurrent && (
                  <Tooltip title={translate("logout_device")}>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(s.id)}
                      size="small"
                    >
                      <Logout />
                    </IconButton>
                  </Tooltip>
                )}
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
